import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import type { Task } from '../store/types';
import { occursOn, recurLabel } from '../lib/recur';
import { addDays, brYmd, norm, today } from '../lib/date';
import { Field, Sheet } from './ui';
import { C } from './theme';
import { t } from '../lib/i18n';
import { useRouter } from 'expo-router';
import { allDocs } from '../store/store';
import { buscarNasAbas } from '../lib/busca';
import { useCores } from './theme';
import { ROLAGEM_TECLADO } from './teclado';

/** Próxima data (a partir de hoje, até 1 ano) em que a tarefa acontece. */
function proxima(tk: Task) {
  let d = today();
  for (let i = 0; i < 366; i++, d = addDays(d, 1)) if (occursOn(tk, d)) return d;
  return null;
}

/** Busca por nome, subtarefas e anotações de todas as tarefas. */
export function SearchSheet({ visible, tasks, cor, onOpen, onClose }:
  { visible: boolean; tasks: Task[]; cor: string; onOpen: (tk: Task, data: string) => void; onClose: () => void }) {
  const [q, setQ] = useState('');
  const router = useRouter();
  const { liberada } = useCores();
  const nasAbas = useMemo(() => (visible ? buscarNasAbas(allDocs(), q, liberada) : []), [q, visible]); // eslint-disable-line react-hooks/exhaustive-deps
  const res = useMemo(() => {
    const k = norm(q.trim());
    if (k.length < 2) return [];
    return tasks.filter(tk => norm(`${tk.title} ${tk.notes} ${tk.subtasks.map(s => s.text).join(' ')}`).includes(k))
      .map(tk => ({ tk, prox: proxima(tk) })).sort((a, b) => (a.prox ?? '9').localeCompare(b.prox ?? '9')).slice(0, 60);
  }, [q, tasks]);
  return (
    <Sheet visible={visible} onClose={() => { setQ(''); onClose(); }} title={t('Buscar')}>
      <View style={{ paddingHorizontal: 16 }}>
        <Field value={q} onChangeText={setQ} placeholder={t('Tarefas e itens de todas as abas')} autoFocus returnKeyType="search" />
      </View>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 8, paddingBottom: 60 }}>
        {q.trim().length >= 2 && !res.length && !nasAbas.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 20 }}>{t('Nada encontrado.')}</Text>}
        {res.map(({ tk, prox }) => (
          <Pressable key={tk.id} onPress={() => { setQ(''); onClose(); setTimeout(() => onOpen(tk, prox ?? tk.start), 300); }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: C.card, borderRadius: 14, padding: 12, borderLeftWidth: 4, borderLeftColor: tk.color ?? cor }}>
            <Text style={{ fontSize: 22 }}>{tk.emoji}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '600' }}>{tk.title}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>
                {prox ? (prox === today() ? t('Hoje') : t('Próxima: {data}', { data: brYmd(prox) })) : t('Última: {data}', { data: brYmd(tk.end ?? tk.start) })}{tk.time ? ` · ${tk.time}` : ''} · {recurLabel(tk.recur, tk.start)}
              </Text>
            </View>
          </Pressable>
        ))}
        {!!nasAbas.length && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('NAS ABAS')}</Text>}
        {nasAbas.map(x => (
          <Pressable key={`${x.aba}:${x.id}`} onPress={() => { setQ(''); onClose(); setTimeout(() => router.push((x.aba === 'anotacoes' ? { pathname: '/anotacoes', params: { item: x.id } } : { pathname: '/aba/[id]', params: { id: x.aba, item: x.id } }) as any), 300); }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
            <Text style={{ fontSize: 22 }}>{x.icone}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={1}>{x.titulo}</Text>
              {!!x.detalhe && <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{x.detalhe}</Text>}
            </View>
          </Pressable>
        ))}
      </ScrollView>
    </Sheet>
  );
}
